import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { TruxTableCell } from 'src/components/trux-table-cell';
import { Constants } from 'src/constants';
import { STOP_LABELS, TrackRow, Trip, TripStopRecord } from '../types';
import { latenessColor, latenessText, tripStatusColor, tripStatusText } from '../trip-utils';
import TrackMap from './track-map';

type TripDrawerProps = {
  row: TrackRow | null;
  timezone: string;
  completingStop: boolean;
  onClose: () => void;
  onEditTrip: (trip: Trip) => void;
  onRequestStatusChange: (trip: Trip, newStatus: number, driverName: string) => void;
  onCompleteStop: (trip: Trip, stopIndex: number) => void;
};

export default function TripDrawer({
  row,
  timezone,
  completingStop,
  onClose,
  onEditTrip,
  onRequestStatusChange,
  onCompleteStop,
}: TripDrawerProps) {
  const trip = row ? row.currentTrip || row.nextTrip : null;
  const driverName = row ? `${row.firstName} ${row.lastName}` : '';
  const firstPendingIndex = trip ? trip.stops.findIndex((stop) => !stop.done) : -1;

  const formatTime = (value: string | null | undefined) =>
    value ? DateTime.fromISO(value).setZone(timezone).toFormat('LLL d, h:mm a') : '—';

  const predictedFor = (index: number, stop: TripStopRecord) => {
    if (stop.actualArrival) {
      return { time: stop.actualArrival, actual: true, slackHours: null as number | null };
    }
    const prediction = trip?.predictedArrivals?.find((p) => p.stopIndex === index);
    if (!prediction) {
      return null;
    }
    return { time: prediction.predictedArrival, actual: false, slackHours: prediction.slackHours };
  };

  return (
    <Drawer anchor="right" open={!!row} onClose={onClose}>
      {row && (
        <Box component="div" sx={{ width: { xs: '100vw', sm: 520 }, p: 2 }}>
          <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
            <Stack sx={{ flexGrow: 1 }}>
              <Typography variant="h6">{driverName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {row.eldVehicleUnitNumber ? `Truck #${row.eldVehicleUnitNumber}` : 'No truck assigned'}
                {' · '}
                {row.connected ? 'connected' : 'disconnected'}
              </Typography>
            </Stack>
            <IconButton onClick={onClose}>
              <Iconify icon="mingcute:close-line" />
            </IconButton>
          </Stack>

          {!trip && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              No planned trips for this driver. Plan one in Route Buddy and create it as a trip.
            </Typography>
          )}

          {trip && (
            <>
              <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                <Label color={tripStatusColor(trip.status)}>{tripStatusText(trip.status)}</Label>
                <Typography variant="subtitle2" sx={{ flexGrow: 1 }} noWrap>
                  {trip.name || `Trip #${trip.id}`}
                </Typography>
                {trip.status === Constants.TRIP_STATUS_STARTED && (
                  <Label color={latenessColor(trip.lateHours)}>{latenessText(trip.lateHours)}</Label>
                )}
              </Stack>

              {trip.status === Constants.TRIP_STATUS_STARTED && (
                <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(trip.progressPercent, 100)}
                    color={trip.lateHours > 0 ? 'warning' : 'success'}
                    sx={{ flexGrow: 1 }}
                  />
                  <Typography variant="caption">{Math.round(trip.progressPercent)}%</Typography>
                </Stack>
              )}

              {trip.status === Constants.TRIP_STATUS_NEW && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Planned to depart {formatTime(trip.plannedDepartAt)}. Starts automatically when the
                  truck gets moving around the departure time.
                </Typography>
              )}

              <TrackMap
                trip={trip}
                truckPosition={row.lat !== null && row.lng !== null ? { lat: row.lat, lng: row.lng } : null}
              />

              {trip.lastLocationAt && trip.status === Constants.TRIP_STATUS_STARTED && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Truck located {DateTime.fromISO(trip.lastLocationAt).toRelative()}, tracked{' '}
                  {trip.lastTrackedAt ? DateTime.fromISO(trip.lastTrackedAt).toRelative() : 'never'}.
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TruxTableCell>Stop</TruxTableCell>
                    <TruxTableCell>Appointment</TruxTableCell>
                    <TruxTableCell>Planned</TruxTableCell>
                    <TruxTableCell>{trip.status === Constants.TRIP_STATUS_NEW ? '' : 'Expected'}</TruxTableCell>
                    {trip.status === Constants.TRIP_STATUS_STARTED && <TruxTableCell />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trip.stops.map((stop, index) => {
                    const predicted = trip.status === Constants.TRIP_STATUS_NEW ? null : predictedFor(index, stop);
                    return (
                      <TableRow key={index} sx={{ opacity: stop.done ? 0.55 : 1 }}>
                        <TruxTableCell>
                          <Stack direction="row" alignItems="center" gap={0.5}>
                            {stop.done && (
                              <Iconify icon="eva:checkmark-circle-2-fill" color="success.main" width={16} />
                            )}
                            <Box component="span">
                              <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                {STOP_LABELS[stop.label]} {index + 1}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 150, display: 'block' }}>
                                {stop.address}
                              </Typography>
                            </Box>
                          </Stack>
                        </TruxTableCell>
                        <TruxTableCell>
                          <Typography variant="caption">{formatTime(stop.appointmentAt)}</Typography>
                        </TruxTableCell>
                        <TruxTableCell>
                          <Typography variant="caption">{formatTime(stop.plannedArrival)}</Typography>
                        </TruxTableCell>
                        <TruxTableCell>
                          {predicted ? (
                            <Typography
                              variant="caption"
                              color={
                                predicted.actual
                                  ? 'text.primary'
                                  : `${latenessColor(
                                      predicted.slackHours !== null ? -predicted.slackHours : null
                                    )}.main`
                              }
                            >
                              {formatTime(predicted.time)}
                              {predicted.actual ? ' (arrived)' : ''}
                            </Typography>
                          ) : (
                            <Typography variant="caption">—</Typography>
                          )}
                        </TruxTableCell>
                        {trip.status === Constants.TRIP_STATUS_STARTED && (
                          <TruxTableCell align="right">
                            {index === firstPendingIndex && (
                              <Button
                                size="small"
                                variant="text"
                                disabled={completingStop}
                                onClick={() => onCompleteStop(trip, index)}
                              >
                                Mark done
                              </Button>
                            )}
                          </TruxTableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <Divider sx={{ my: 2 }} />

              <Stack direction="row" gap={1} justifyContent="flex-end">
                {(trip.status === Constants.TRIP_STATUS_NEW ||
                  trip.status === Constants.TRIP_STATUS_STARTED) && (
                  <Button
                    variant="outlined"
                    startIcon={<Iconify icon="eva:edit-outline" />}
                    onClick={() => onEditTrip(trip)}
                  >
                    {trip.status === Constants.TRIP_STATUS_STARTED ? 'Replan' : 'Edit Plan'}
                  </Button>
                )}
                {trip.status === Constants.TRIP_STATUS_NEW && (
                  <Button
                    variant="contained"
                    startIcon={<Iconify icon="eva:play-circle-outline" />}
                    onClick={() => onRequestStatusChange(trip, Constants.TRIP_STATUS_STARTED, driverName)}
                  >
                    Start Now
                  </Button>
                )}
                {trip.status === Constants.TRIP_STATUS_STARTED && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<Iconify icon="eva:checkmark-circle-2-outline" />}
                    onClick={() => onRequestStatusChange(trip, Constants.TRIP_STATUS_FINISHED, driverName)}
                  >
                    Mark Finished
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Iconify icon="eva:close-circle-outline" />}
                  onClick={() => onRequestStatusChange(trip, Constants.TRIP_STATUS_ABANDONED, driverName)}
                >
                  Abandon
                </Button>
              </Stack>
            </>
          )}
        </Box>
      )}
    </Drawer>
  );
}
